import React from "react";

import {
  CopyToClipboard,
  EventAvailableIcon,
  EventIcon,
  IntegrationInstructionsIcon,
  RotateRightIcon,
  Table,
  TableCellType,
} from "@prequel/shared-components";

import DataIntegrityStatusBadge, {
  DataIntegrityStatus,
  getDataIntegrityStatusBadgeColor,
  isDataIntegrityStatusBadgeActive,
} from "@/components/DataIntegrityStatusBadge";
import Timestamp from "@/components/Timestamp";
import TransferStatusBadge, { TransferStatus } from "@/components/TransferStatusBadge";
import { DataIntegrityJobDetails } from "@/types";

type IntegrityDetailsSectionProps = {
  integrity: DataIntegrityJobDetails;
};

const IntegrityDetailsSection = ({ integrity }: IntegrityDetailsSectionProps) => {
  const modelRows =
    integrity.data_integrity_results?.map((result) => {
      const status = result.is_healthy
        ? DataIntegrityStatus.HEALTHY
        : result.error_message?.includes("does not support data integrity checks") ||
            integrity.status === TransferStatus.ERROR
          ? DataIntegrityStatus.UNKNOWN
          : DataIntegrityStatus.UNHEALTHY;
      return {
        id: result.model_name,
        cells: [
          { value: result.model_name },
          {
            value: (
              <DataIntegrityStatusBadge
                status={status}
                color={getDataIntegrityStatusBadgeColor(status)}
                isActive={isDataIntegrityStatusBadgeActive(status)}
              />
            ),
          },
          { value: result.sampling_rate || "N/A" },
          {
            value: result.total_rows_compared.toLocaleString(),
          },
          { value: result.total_rows_missing.toLocaleString() },
          { value: result.total_rows_incorrect.toLocaleString() },
          {
            type: TableCellType.OVERFLOW,
            value: result.error_message ? (
              <div className="min-width-[350px] font-mono text-xs">{result.error_message}</div>
            ) : (
              ""
            ),
          },
        ],
      };
    }) ?? [];

  const columns = [
    "Model Name",
    "Status",
    "Sampling Rate",
    "Total rows compared",
    "Rows missing",
    "Rows incorrect",
    "Error",
  ];

  return (
    <div>
      <SectionContent integrity={integrity} />
      <h2 className="mt-8 mb-4 text-xl font-semibold">Models Compared ({modelRows.length})</h2>
      <Table columns={columns} rows={modelRows} footer={`${modelRows.length} models`} />
    </div>
  );
};

const SectionContent = ({ integrity }: IntegrityDetailsSectionProps) => {
  const SectionItem = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) => (
    <div className="grid grid-cols-8 items-start gap-x-4 gap-y-2">
      <div className="text-ashy flex items-center">
        {icon}
        <span className="icon-class mr-2"></span>
        {label}
      </div>
      <div className="col-span-3 mt-0.5 flex text-sm">{value}</div>
    </div>
  );

  const allModelErrorsMissing = integrity.data_integrity_results?.every((result) => !result.error_message) ?? true;

  return (
    <div className="space-y-4 py-4">
      <SectionItem
        icon={<IntegrationInstructionsIcon />}
        label="Job id"
        value={
          integrity.id ? (
            <>
              <CopyToClipboard textToCopy={integrity.id} />
              <span className="rounded-sm bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-600">
                <span className="mr-2">{integrity.id}</span>
              </span>
            </>
          ) : (
            "N/A"
          )
        }
      />
      <SectionItem
        icon={<RotateRightIcon />}
        label="Status"
        value={<TransferStatusBadge status={integrity.status} />}
      />
      <SectionItem icon={<EventIcon />} label="Submitted" value={<Timestamp timestamp={integrity.submitted_at} />} />
      <SectionItem
        icon={<EventAvailableIcon />}
        label="Completed"
        value={integrity.ended_at ? <Timestamp timestamp={integrity.ended_at} /> : "N/A"}
      />
      {integrity.status === TransferStatus.ERROR && allModelErrorsMissing && (
        <SectionItem
          icon={<EventIcon />}
          label="Error"
          value={
            <div className="rounded-sm bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-600">
              {integrity.log_message || "Unknown error"}
            </div>
          }
        />
      )}
    </div>
  );
};

export default IntegrityDetailsSection;
